import { figureQuestion, type AssistantMessage, type FigureId } from '@cockpit/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ApiError } from '../api';
import { assistantApi, streamChat } from '../api/assistant';

/** Message affiché, avec l'état du flux pour la réponse en cours. */
export interface ChatEntry extends AssistantMessage {
  key: number;
  pending?: boolean;
  /** « Je consulte les chiffres clés… » pendant la lecture des outils. */
  status?: string;
  error?: boolean;
}

/**
 * Tiroir de l'assistant (maquette `#drawer`) : ouvert depuis n'importe quel écran, une conversation par
 * session d'ouverture, suggestions calculées par le serveur, réponses en flux.
 */
export const useAssistantStore = defineStore('assistant', () => {
  const open = ref(false);
  const conversationId = ref<string | null>(null);
  const entries = ref<ChatEntry[]>([]);
  const suggestions = ref<string[]>([]);
  const busy = ref(false);
  let nextKey = 1;
  let controller: AbortController | null = null;

  async function loadSuggestions(): Promise<void> {
    try {
      suggestions.value = (await assistantApi.suggestions()).items;
    } catch {
      suggestions.value = [];
    }
  }

  /** La conversation en cours survit à un rechargement de la page (onglet seulement). */
  const STORAGE_KEY = 'cockpit.assistant.conversation';
  function remember(id: string | null): void {
    try {
      if (id) sessionStorage.setItem(STORAGE_KEY, id);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* stockage indisponible */
    }
  }
  async function restore(): Promise<void> {
    let id: string | null = null;
    try {
      id = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      id = null;
    }
    if (!id || entries.value.length > 0) return;
    try {
      const conversation = await assistantApi.conversation(id);
      if (entries.value.length > 0) return;
      conversationId.value = conversation.id;
      entries.value = conversation.messages.map((m) => ({ ...m, key: nextKey++ }));
    } catch {
      remember(null);
    }
  }

  function show(): void {
    open.value = true;
    void loadSuggestions();
    void restore();
  }

  function hide(): void {
    open.value = false;
  }

  function reset(): void {
    controller?.abort();
    conversationId.value = null;
    entries.value = [];
    busy.value = false;
    remember(null);
  }

  function patch(key: number, change: Partial<ChatEntry>): void {
    entries.value = entries.value.map((e) => (e.key === key ? { ...e, ...change } : e));
  }

  async function ask(question: string, options: { figureId?: FigureId } = {}): Promise<void> {
    const message = question.trim();
    if (!message || busy.value) return;
    busy.value = true;
    const at = new Date().toISOString();
    entries.value = [
      ...entries.value,
      { key: nextKey++, role: 'user', text: message, proposals: [], tools: [], at },
    ];
    const key = nextKey++;
    entries.value = [
      ...entries.value,
      {
        key,
        role: 'assistant',
        text: '',
        proposals: [],
        tools: [],
        at,
        pending: true,
        status: 'Claude réfléchit…',
      },
    ];
    controller = new AbortController();
    let text = '';
    try {
      await streamChat(
        {
          message,
          ...(conversationId.value ? { conversationId: conversationId.value } : {}),
          ...(options.figureId ? { figureId: options.figureId } : {}),
        },
        (event) => {
          if (event.type === 'conversation') {
            conversationId.value = event.conversationId;
            remember(event.conversationId);
          } else if (event.type === 'tool') patch(key, { status: `${event.label}…` });
          else if (event.type === 'delta') {
            text += event.text;
            patch(key, { text, status: undefined });
          } else if (event.type === 'done')
            patch(key, { ...event.message, pending: false, status: undefined });
          else if (event.type === 'error')
            patch(key, { text: event.message, pending: false, status: undefined, error: true });
        },
        { signal: controller.signal },
      );
      const current = entries.value.find((e) => e.key === key);
      if (current?.pending) patch(key, { pending: false, status: undefined });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      patch(key, {
        text: err instanceof ApiError ? err.message : "L'assistant n'a pas pu répondre.",
        pending: false,
        status: undefined,
        error: true,
      });
    } finally {
      busy.value = false;
      controller = null;
    }
  }

  /** Lot AD : « Comment c'est calculé ? » : ouvre le tiroir et pose la question avec l'identifiant du chiffre. */
  async function explain(figureId: FigureId): Promise<void> {
    show();
    if (busy.value) return;
    await ask(figureQuestion(figureId), { figureId });
  }

  return {
    open,
    conversationId,
    entries,
    suggestions,
    busy,
    show,
    hide,
    reset,
    ask,
    explain,
    loadSuggestions,
  };
});
